import {
  cloneElement,
  useId,
  type AriaAttributes,
  type ReactElement,
  type ReactNode
} from 'react';

interface LwFieldBaseProps {
  label?: ReactNode;
  span?: 'full' | '2';
  hint?: ReactNode;
  error?: ReactNode;
  readonly?: boolean;
  htmlFor?: string;
}

type LwFieldLegacyProps = LwFieldBaseProps & {
  children: ReactNode;
  presentation?: never;
  caps?: never;
  required?: never;
  className?: never;
};

interface LwCanonicalControlProps {
  id?: string;
  required?: boolean;
  'aria-describedby'?: string;
  'aria-invalid'?: AriaAttributes['aria-invalid'];
}

type LwFieldCanonicalProps = LwFieldBaseProps & {
  children: ReactElement<LwCanonicalControlProps>;
  presentation: 'canonical';
  caps?: boolean;
  required?: boolean;
  className?: string;
};

export type LwFieldProps = LwFieldLegacyProps | LwFieldCanonicalProps;

/**
 * Form field wrapper — label + control + optional hint/error, styled by the
 * kit's `.field` rules. Put a native input/select/textarea (or a readonly node)
 * as the child; the kit CSS styles it.
 */
export function LwField(props: LwFieldProps) {
  const { label, children, span, hint, error, readonly, htmlFor } = props;
  const cls = `field${span === 'full' ? ' full' : span === '2' ? ' span2' : ''}`;
  const generatedId = useId();

  if (props.presentation !== 'canonical') {
    return (
      <div className={cls}>
        {label && <label htmlFor={htmlFor}>{label}</label>}
        {readonly ? <div className="stage-readonly">{children}</div> : children}
        {hint && <span className="hint">{hint}</span>}
        {error && <span className="err">{error}</span>}
      </div>
    );
  }

  const canonicalChildren = props.children;
  const controlId = htmlFor ?? canonicalChildren.props.id ?? `${generatedId}-control`;
  const hintId = hint ? `${generatedId}-hint` : undefined;
  const errorId = error ? `${generatedId}-error` : undefined;
  const describedBy = [canonicalChildren.props['aria-describedby'], hintId, errorId]
    .filter(Boolean)
    .join(' ') || undefined;
  const control = cloneElement(canonicalChildren, {
    id: controlId,
    required: props.required || canonicalChildren.props.required || undefined,
    'aria-describedby': describedBy,
    'aria-invalid': error ? true : canonicalChildren.props['aria-invalid']
  });

  return (
    <div className={`${cls} lw-field${props.className ? ` ${props.className}` : ''}`}>
      {label && (
        <label htmlFor={controlId} className={props.caps ? 'lw-caps-label' : undefined}>
          {label}
          {props.required && <span className="req" aria-hidden="true"> *</span>}
        </label>
      )}
      {readonly ? <div className="stage-readonly">{control}</div> : control}
      {hint && <span id={hintId} className="hint">{hint}</span>}
      {error && <span id={errorId} className="err" role="alert">{error}</span>}
    </div>
  );
}
